<script setup lang="ts">
import { defineProps } from "vue";
import AppTooltip from "@/components/AppTooltip.vue";
import { Pillar } from "@/types/calculators";
import { elColor } from "@/constants/colors";
import { elementStageNames, tenGods, ownerOrder } from "@/constants/bazi";
import { useI18n } from "vue-i18n";

defineProps<{ pillars: Record<string, Pillar> }>();

const { t } = useI18n();
</script>

<template>
  <table class="table-auto text-center align-middle w-full">
    <thead
      class="bg-brown-gradient text-white text-xl leading-[24px] font-semibold"
    >
      <tr class="h-12">
        <th class="w-1/4">
          {{ t("calculators.bazi.results.birthday-map.times.hour") }}
        </th>
        <th class="w-1/4">
          {{ t("calculators.bazi.results.birthday-map.times.day") }}
        </th>
        <th class="w-1/4">
          {{ t("calculators.bazi.results.birthday-map.times.month") }}
        </th>
        <th class="w-1/4">
          {{ t("calculators.bazi.results.birthday-map.times.year") }}
        </th>
      </tr>
    </thead>
    <tbody>
      <!-- HEAVEN STEM -->
      <tr>
        <td
          v-for="key in ownerOrder"
          :key="key"
          class="border px-3 py-3 align-middle"
        >
          <p>
            {{ t("calculators.bazi.results.birthday-map.stem.heaven-stem") }}
          </p>
          <AppTooltip>
            <template #trigger>
              <h4 :class="elColor[pillars[key].element]">
                {{ pillars[key].stem }}
              </h4>
            </template>
            <template #popper>
              <div class="max-w-96">
                <h5>{{ pillars[key].labels.stem }}</h5>
                <div class="text-left">{{ pillars[key].tips.stem }}</div>
              </div>
            </template>
          </AppTooltip>
        </td>
      </tr>

      <!-- EARTH BRANCH -->
      <tr>
        <td
          v-for="key in ownerOrder"
          :key="key"
          class="border px-3 py-3 align-middle"
        >
          <p>
            {{ t("calculators.bazi.results.birthday-map.stem.earth-branch") }}
          </p>
          <AppTooltip>
            <template #trigger>
              <h4 class="text-brown">{{ pillars[key].branch }}</h4>
            </template>
            <template #popper>
              <div class="max-w-96">
                <h5>{{ pillars[key].labels.branch }}</h5>
                <div class="text-left">{{ pillars[key].tips.branch }}</div>
              </div>
            </template>
          </AppTooltip>
        </td>
      </tr>

      <!-- STAGE -->
      <tr>
        <td
          v-for="key in ownerOrder"
          :key="key"
          class="border px-3 py-3 align-middle"
        >
          <p>
            {{ t("calculators.bazi.results.birthday-map.stem.stage") }}
          </p>
          <AppTooltip>
            <template #trigger>
              <h4
                class="mb-0"
                :style="`color: ${
                  elementStageNames[pillars[key].stage]['color']
                }`"
              >
                {{ pillars[key].stage }}
              </h4>
            </template>
            <template #popper>
              <div class="max-w-96">
                <h5>{{ pillars[key].labels.stage }}</h5>
                <div class="text-left">{{ pillars[key].tips.stage }}</div>
              </div>
            </template>
          </AppTooltip>
        </td>
      </tr>

      <!-- ELEMENT -->
      <tr>
        <td
          v-for="key in ownerOrder"
          :key="key"
          class="border px-2 py-2 align-middle"
        >
          <p>
            {{ t("calculators.bazi.results.birthday-map.stem.element") }}
          </p>
          <AppTooltip>
            <template #trigger>
              <img
                :src="
                  require(`@/assets/calculators/bazi/${pillars[key].element}.png`)
                "
                :alt="pillars[key].element"
                class="my-1 size-10 mx-auto"
              />
            </template>
            <template #popper>
              <div class="max-w-96 text-left">
                {{ pillars[key].tips.element }}
              </div>
            </template>
          </AppTooltip>
        </td>
      </tr>

      <!-- GOD -->
      <tr>
        <td
          v-for="key in ownerOrder"
          :key="key"
          class="border px-3 py-3 align-middle"
        >
          <p>
            {{ t("calculators.bazi.results.birthday-map.stem.god") }}
          </p>
          <AppTooltip>
            <template #trigger>
              <h4
                class="mb-0"
                :style="`color: ${tenGods[pillars[key].god]['color']}`"
              >
                {{ pillars[key].god }}
              </h4>
            </template>
            <template #popper>
              <div class="max-w-96">
                <h5>{{ pillars[key].labels.god }}</h5>
                <p class="text-left">{{ pillars[key].tips.god }}</p>
              </div>
            </template>
          </AppTooltip>
        </td>
      </tr>
    </tbody>
  </table>
</template>
